import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import {
  EXPERTISE_OPTIONS,
  INDUSTRY_BACKGROUNDS,
  ASSESSMENT_VOLUMES,
  GEOGRAPHIC_OPTIONS,
  LANGUAGE_OPTIONS,
  getReviewerProfile,
  completeProfile,
  updateProfile,
  type ReviewerProfile,
  type CompleteProfileData,
} from '../api/reviewers';
import type { User } from '../App';
import { Loader2, FileCheck } from 'lucide-react';

interface ReviewerCompleteProfileProps {
  user: User;
  mode: 'complete' | 'edit';
  onSuccess: () => void;
  onBack?: () => void;
}

export function ReviewerCompleteProfile({ user, mode, onSuccess, onBack }: ReviewerCompleteProfileProps) {
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ReviewerProfile | null>(null);

  const [primary_expertise, setPrimaryExpertise] = useState<string[]>([]);
  const [secondary_expertise, setSecondaryExpertise] = useState<string[]>([]);
  const [years_experience, setYearsExperience] = useState<number>(0);
  const [industry_background, setIndustryBackground] = useState<string>('');
  const [geographic_focus, setGeographicFocus] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [assessment_volume, setAssessmentVolume] = useState<string>('Moderate');
  const [availability_status, setAvailabilityStatus] = useState<string>('available');
  const [conflictText, setConflictText] = useState('');
  const [bio, setBio] = useState('');

  useEffect(() => {
    if (mode !== 'edit') return;
    (async () => {
      try {
        const res = await getReviewerProfile();
        const p = res.profile;
        setProfile(p);
        setPrimaryExpertise(Array.isArray(p.primary_expertise) ? p.primary_expertise : []);
        setSecondaryExpertise(Array.isArray(p.secondary_expertise) ? p.secondary_expertise : []);
        setYearsExperience(Number(p.years_experience) || 0);
        setIndustryBackground(p.industry_background || '');
        setGeographicFocus(Array.isArray(p.geographic_focus) ? p.geographic_focus : []);
        setLanguages(Array.isArray(p.languages) ? p.languages : []);
        setAssessmentVolume(p.assessment_volume || 'Moderate');
        setAvailabilityStatus(p.availability_status || 'available');
        setBio(p.bio || '');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  const toggle = (arr: string[], item: string, set: (a: string[]) => void) => {
    if (arr.includes(item)) set(arr.filter((x) => x !== item));
    else set([...arr, item]);
  };

  const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (primary_expertise.length === 0) {
      setError('Select at least one primary expertise area.');
      return;
    }
    const rawConflicts = conflictText.trim() ? conflictText.trim().split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [];
    const conflicted_institutions = rawConflicts.length ? rawConflicts.filter(isUuid) : undefined;
    if (rawConflicts.length > 0 && conflicted_institutions?.length === 0) {
      setError('Conflict of interest must be institution IDs (UUIDs). Names are not accepted.');
      return;
    }
    const data: CompleteProfileData = {
      primary_expertise,
      secondary_expertise: secondary_expertise.length ? secondary_expertise : undefined,
      years_experience,
      industry_background: industry_background || undefined,
      geographic_focus: geographic_focus.length ? geographic_focus : undefined,
      languages: languages.length ? languages : undefined,
      assessment_volume,
      availability_status: availability_status || undefined,
      conflicted_institutions,
      bio: bio.trim() || undefined,
    };
    setSaving(true);
    try {
      if (mode === 'complete') {
        await completeProfile(data);
      } else {
        await updateProfile(data);
      }
      onSuccess();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string; details?: unknown } }; message?: string };
      const msg = err.response?.data?.message || err.response?.data?.error || (e instanceof Error ? e.message : 'Failed to save profile');
      const details = err.response?.data?.details;
      setError(details && Array.isArray(details) && details.length > 0
        ? `${msg}: ${(details as { msg?: string }[]).map((d) => d.msg || JSON.stringify(d)).join(', ')}`
        : msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="border-[rgba(201,168,76,0.2)]">
        <CardHeader>
          <CardTitle className="text-[var(--navy)] font-display flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-[var(--gold)]" />
            {mode === 'complete' ? 'Complete your reviewer profile' : 'Edit reviewer profile'}
          </CardTitle>
          <CardDescription className="text-[var(--navy)]/80">
            {mode === 'complete'
              ? 'This helps administrators assign you relevant innovations. Complete all required fields (*).'
              : 'Update your expertise and availability.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-[var(--navy)]">Primary expertise *</Label>
              <p className="text-sm text-[var(--navy)]/60 mb-2">Select at least one</p>
              <div className="flex flex-wrap gap-2">
                {EXPERTISE_OPTIONS.map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={primary_expertise.includes(opt)}
                      onChange={() => toggle(primary_expertise, opt, setPrimaryExpertise)}
                      className="rounded border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[var(--navy)]">Secondary expertise</Label>
              <div className="flex flex-wrap gap-2">
                {EXPERTISE_OPTIONS.filter((o) => !primary_expertise.includes(o)).map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={secondary_expertise.includes(opt)}
                      onChange={() => toggle(secondary_expertise, opt, setSecondaryExpertise)}
                      className="rounded border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="years_experience" className="text-[var(--navy)]">Years of experience in innovation assessment *</Label>
              <Input
                id="years_experience"
                type="number"
                min={0}
                value={years_experience}
                onChange={(e) => setYearsExperience(parseInt(e.target.value, 10) || 0)}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-[var(--navy)]">Industry background</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {INDUSTRY_BACKGROUNDS.map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="industry"
                      checked={industry_background === opt}
                      onChange={() => setIndustryBackground(opt)}
                      className="border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[var(--navy)]">Geographic focus</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {GEOGRAPHIC_OPTIONS.map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={geographic_focus.includes(opt)}
                      onChange={() => toggle(geographic_focus, opt, setGeographicFocus)}
                      className="rounded border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[var(--navy)]">Languages</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LANGUAGE_OPTIONS.map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={languages.includes(opt)}
                      onChange={() => toggle(languages, opt, setLanguages)}
                      className="rounded border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[var(--navy)]">Assessment volume preference *</Label>
              <p className="text-sm text-[var(--navy)]/60 mb-2">Roughly how many assessments per week you can take</p>
              <div className="flex flex-wrap gap-3">
                {ASSESSMENT_VOLUMES.map((opt) => (
                  <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="volume"
                      checked={assessment_volume === opt}
                      onChange={() => setAssessmentVolume(opt)}
                      className="border-[var(--navy)]/30"
                    />
                    <span className="text-sm text-[var(--navy)]">
                      {opt === 'Light' && 'Light (1–3/week)'}
                      {opt === 'Moderate' && 'Moderate (4–7/week)'}
                      {opt === 'Heavy' && 'Heavy (8+/week)'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="conflict" className="text-[var(--navy)]">Conflict of interest</Label>
              <p className="text-sm text-[var(--navy)]/60 mb-1">Institution IDs (UUIDs) you cannot review — one per line or comma-separated</p>
              <Textarea
                id="conflict"
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                value={conflictText}
                onChange={(e) => setConflictText(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="bio" className="text-[var(--navy)]">Brief professional background (optional, max 500 chars)</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, 500))}
                maxLength={500}
                rows={4}
                className="mt-1"
              />
              <p className="text-xs text-[var(--navy)]/60 mt-1">{bio.length}/500</p>
            </div>

            <div className="flex gap-3 pt-4">
              {onBack && (
                <Button type="button" variant="outline" onClick={onBack}>
                  Back
                </Button>
              )}
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {mode === 'complete' ? 'Complete profile' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
